import React, { useState, useEffect } from 'react';
import Navbar from './components/Navbar';
import Forum from './components/Forum';
import Admin from './components/Admin';
import LeftSidebar from './components/LeftSidebar';
import RightSidebar from './components/RightSidebar';
import { getCurrentUser, ensureProfile, getReferralCode } from './services/authStore';
import { supabase } from './services/supabaseClient';

function App() {
  const [currentPage, setCurrentPage] = useState('forum'); // Default to forum
  const [currentUser, setCurrentUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [referralCode, setReferralCode] = useState('');
  const [showLeftSidebar, setShowLeftSidebar] = useState(true);
  const [showRightSidebar, setShowRightSidebar] = useState(true);

  useEffect(() => {
    // Handle OAuth callback first
    const handleOAuthCallback = async () => {
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const accessToken = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token');
      
      if (accessToken && refreshToken) {
        const { error } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken
        });
        
        if (error) {
          console.error('OAuth callback: setSession error =', error);
        }
        
        await new Promise(resolve => setTimeout(resolve, 500));
        await loadUser();
        window.history.replaceState({}, document.title, window.location.pathname);
        return;
      }
      
      // No OAuth callback, load user normally
      await loadUser();
    };

    handleOAuthCallback();
    
    // Push initial state
    window.history.replaceState({ page: 'forum' }, '', window.location.pathname);

    // Listen for browser back/forward buttons
    const handlePopState = (event) => {
      const page = event.state?.page || 'forum';
      setCurrentPage(page);
      window.scrollTo(0, 0);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadUser = async () => {
    const user = await getCurrentUser();
    setCurrentUser(user);
    if (user) {
      // Ensure profile exists and get it
      const userProfile = await ensureProfile(user);
      setProfile(userProfile);
      const code = await getReferralCode(user.id);
      setReferralCode(code || '');
    }
  };

  const handleNavigateToAdmin = () => {
    setCurrentPage('admin');
    window.history.pushState({ page: 'admin' }, '', window.location.pathname);
    window.scrollTo(0, 0);
  };

  const handleNavigateToForum = () => {
    setCurrentPage('forum');
    window.history.pushState({ page: 'forum' }, '', window.location.pathname);
    window.scrollTo(0, 0);
  };

  const handleShare = async () => {
    if (!referralCode) return;
    const siteUrl = process.env.REACT_APP_SITE_URL || window.location.origin;
    const link = `${siteUrl}/?ref=${referralCode}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: 'DevVital', text: 'Join DevVital - Optimize Your Human Hardware', url: link });
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
        alert('Link copied to clipboard!');
      }
    } catch (err) {
      console.error('Share error:', err);
    }
  };

  const handleSignOut = () => {
    window.location.reload();
  };

  const handleToggleRightSidebar = () => {
    setShowRightSidebar(!showRightSidebar);
  };

  const handleAuthSuccess = async () => {
    await loadUser();
    setShowRightSidebar(true);
  };

  // Responsive: hide sidebars on small screens
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) {
        setShowLeftSidebar(false);
        setShowRightSidebar(false);
      } else {
        setShowLeftSidebar(true);
        setShowRightSidebar(true);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="App flex flex-col min-h-screen bg-black">
      <Navbar 
        onHomeClick={handleNavigateToForum} 
        onAdminClick={handleNavigateToAdmin}
        onToggleRightSidebar={handleToggleRightSidebar}
      />
      
      {/* Main layout container with flexbox */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left Sidebar - Desktop: flex, Mobile: fixed overlay */}
        {showLeftSidebar && currentPage === 'forum' && (
          <div className="hidden lg:block flex-shrink-0">
            <LeftSidebar currentUser={currentUser} referralCode={referralCode} />
          </div>
        )}
        {showLeftSidebar && currentPage === 'forum' && (
          <div className="lg:hidden fixed inset-0 top-16 z-40 bg-black/50 backdrop-blur-sm">
            <LeftSidebar currentUser={currentUser} referralCode={referralCode} onClose={() => setShowLeftSidebar(false)} />
          </div>
        )}
        
        {/* Center content - flexes to fill available space */}
        <div className="flex-1 overflow-y-auto bg-black transition-all duration-300">
          {currentPage === 'forum' ? (
            <Forum />
          ) : (
            <Admin onBackClick={handleNavigateToForum} />
          )}
        </div>
        
        {/* Right Sidebar - Desktop: flex, Mobile: fixed overlay */}
        {showRightSidebar && currentPage === 'forum' && (
          <div className="hidden lg:block flex-shrink-0">
            <RightSidebar
              currentUser={currentUser}
              profile={profile}
              onAuthSuccess={handleAuthSuccess}
              referralCode={referralCode}
              onShare={handleShare}
              onSignOut={handleSignOut}
              onAdminClick={handleNavigateToAdmin}
            />
          </div>
        )}
        {showRightSidebar && currentPage === 'forum' && (
          <div className="lg:hidden fixed inset-0 top-16 right-0 z-40 bg-black/50 backdrop-blur-sm flex justify-end pointer-events-none">
            <div className="pointer-events-auto">
              <RightSidebar
                currentUser={currentUser}
                profile={profile}
                onAuthSuccess={handleAuthSuccess}
                referralCode={referralCode}
                onShare={handleShare}
                onSignOut={handleSignOut}
                onAdminClick={handleNavigateToAdmin}
                onClose={() => setShowRightSidebar(false)}
              />
            </div>
          </div>
        )}
      </div>

      {/* Mobile: Bottom toggle buttons for sidebars */}
      {currentPage === 'forum' && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 lg:hidden flex gap-2 z-50 bg-black/80 backdrop-blur px-2 py-1 rounded-full shadow-lg">
          <button
            onClick={() => setShowLeftSidebar(!showLeftSidebar)}
            className="px-3 py-2 bg-green-btn text-black rounded-full text-sm font-bold hover:opacity-90 transition"
          >
            📋 {showLeftSidebar ? 'Close' : 'Widgets'}
          </button>
          <button
            onClick={() => setShowRightSidebar(!showRightSidebar)}
            className="px-3 py-2 bg-green-btn text-black rounded-full text-sm font-bold hover:opacity-90 transition"
          >
            {currentUser ? '👤' : '🔐'} {showRightSidebar ? 'Close' : (currentUser ? 'Profile' : 'Sign In')}
          </button>
        </div>
      )}
    </div>
  );
}

export default App;
